import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

export default function CommentsThread({ chapterId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", comment: "" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/comments", { params: { chapter_id: chapterId } });
      setComments(res.data || []);
    } finally {
      setLoading(false);
    }
    // `api` is a module-level axios instance and setters from useState are stable; only chapterId truly drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.comment) {
      toast.error("Please fill all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/comments", { chapter_id: chapterId, ...form });
      setForm({ name: "", email: "", comment: "" });
      toast.success("Thank you — your reflection has been posted.");
      load();
    } catch (err) {
      toast.error("Could not post your comment. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-16" data-testid="comments-section">
      <div className="flex items-center gap-3 mb-6">
        <MessageCircle className="h-4 w-4 text-secondary" />
        <h3 className="font-display text-2xl text-foreground">Reflections from readers</h3>
      </div>

      <form onSubmit={submit} data-testid="comment-form" className="bg-card border border-border rounded-2xl p-6 md:p-8 space-y-4 shadow-sm">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            data-testid="comment-name"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="font-ui bg-background"
          />
          <Input
            data-testid="comment-email"
            type="email"
            placeholder="Email (kept private)"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="font-ui bg-background"
          />
        </div>
        <Textarea
          data-testid="comment-text"
          placeholder="What did this truth stir in you?"
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          rows={4}
          className="font-body bg-background"
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={submitting} data-testid="comment-submit" className="rounded-full font-ui">
            {submitting ? "Posting…" : "Post reflection"}
          </Button>
        </div>
      </form>

      <div className="mt-8 space-y-6" data-testid="comment-list">
        {loading && <p className="text-muted-foreground font-ui text-sm">Loading reflections…</p>}
        {!loading && comments.length === 0 && (
          <p className="font-body italic text-muted-foreground">
            Be the first to leave a reflection on this truth.
          </p>
        )}
        {comments.map((c) => (
          <article key={c.id} className="border-l-2 border-primary pl-5 py-1" data-testid={`comment-${c.id}`}>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-display text-lg text-foreground">{c.name}</span>
              <span className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </span>
            </div>
            <p className="mt-2 font-body leading-relaxed text-foreground/90 whitespace-pre-wrap">{c.comment}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
